package ai.beakman.undercover.web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import ai.beakman.undercover.data.Themes
import ai.beakman.undercover.game.Avatars
import ai.beakman.undercover.game.GameLogic
import ai.beakman.undercover.i18n.I18n
import ai.beakman.undercover.model.GameResult
import ai.beakman.undercover.model.Vote
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

private const val MAX_PLAYERS = 12
private const val MAX_NAME_LENGTH = 20

private enum class LocalScreen { NAMES, THEME, RULES, REVEAL, TIMER, VOTE, RESULT }

/** Estado do MODO LOCAL (espelho do LocalGameModel de shared/ui/UndercoverApp.kt). */
private class LocalModel {
    var screen by mutableStateOf(LocalScreen.NAMES)
    val players = mutableStateListOf<String>()
    var themeId by mutableStateOf(Themes.ALL.first().id)
    var numInfiltrators by mutableStateOf(1)
    var timerSeconds by mutableStateOf(180)

    // rodada atual (ids de jogador == nomes, no modo local)
    var word by mutableStateOf("")
    var hint by mutableStateOf("")
    var infiltrators by mutableStateOf<List<String>>(emptyList())
    var order by mutableStateOf<List<String>>(emptyList())
    var revealIndex by mutableStateOf(0)
    val votes = mutableStateListOf<Vote>()
    var result by mutableStateOf<GameResult?>(null)

    val theme get() = Themes.byId(themeId) ?: Themes.ALL.first()
    fun maxInfiltrators() = GameLogic.maxInfiltrators(players.size)

    fun startRound() {
        val setup = GameLogic.setupRound(theme, players.toList(), numInfiltrators)
        word = setup.word
        hint = setup.hint
        infiltrators = setup.infiltratorIds
        order = players.toList()
        revealIndex = 0
        votes.clear()
        result = null
        screen = LocalScreen.REVEAL
    }

    fun finishVoting() {
        result = GameLogic.computeResult(order, votes.toList(), infiltrators)
        screen = LocalScreen.RESULT
    }
}

/** Modo Local (um device passa de mão em mão): NAMES → THEME → RULES → REVEAL → TIMER → VOTE → RESULT. */
@Composable
fun LocalFlow(onExit: () -> Unit) {
    val model = remember { LocalModel() }
    when (model.screen) {
        LocalScreen.NAMES -> LocalNamesScreen(model, onExit)
        LocalScreen.THEME -> LocalThemeScreen(model)
        LocalScreen.RULES -> LocalRulesScreen(model)
        LocalScreen.REVEAL -> LocalRevealScreen(model, onExit)
        LocalScreen.TIMER -> LocalTimerScreen(model, onExit)
        LocalScreen.VOTE -> LocalVoteScreen(model, onExit)
        LocalScreen.RESULT -> LocalResultScreen(model)
    }
}

// ---------------------------------------------------------------- NAMES

@Composable
private fun LocalNamesScreen(model: LocalModel, onExit: () -> Unit) {
    val s = I18n.strings
    var input by remember { mutableStateOf("") }
    val canNext = model.players.size >= GameLogic.MIN_PLAYERS

    fun addPlayer() {
        val n = input.trim()
        if (n.isNotEmpty() &&
            model.players.none { it.equals(n, ignoreCase = true) } &&
            model.players.size < MAX_PLAYERS
        ) {
            model.players.add(n)
            input = ""
        }
    }

    Div({ classes("screen-body") }) {
        BackLink(s.backTrocarModo) { onExit() }
        StepIndicator(1)
        H2({ classes("title") }) { Text(s.playersCount(model.players.size)) }
        Div({
            classes("muted")
            style { property("margin", "6px 0 12px") }
        }) { Text(s.namesAddHint) }

        Div({ classes("row") }) {
            Input(InputType.Text) {
                classes("input")
                style {
                    property("flex", "1")
                    property("min-width", "0")
                }
                value(input)
                placeholder(s.namesPlayerPlaceholder)
                onInput { event -> input = event.value.take(MAX_NAME_LENGTH) }
                onKeyDown { event -> if (event.key == "Enter") addPlayer() }
            }
            Button({
                style {
                    property("flex", "none")
                    property("width", "52px")
                    property("height", "52px")
                    property("background", "var(--primary)")
                    property("color", "var(--on-primary)")
                    property("border", "none")
                    property("border-radius", "14px")
                    property("font-size", "26px")
                    property("font-weight", "700")
                    property("font-family", "inherit")
                    property("cursor", "pointer")
                }
                onClick { addPlayer() }
            }) { Text("+") }
        }

        Div({
            classes("grow")
            style {
                property("margin", "12px 0")
                property("overflow-y", "auto")
            }
        }) {
            model.players.forEach { name ->
                Div({
                    classes("row")
                    style {
                        property("background", "var(--card)")
                        property("border-radius", "var(--radius-pill)")
                        property("padding", "10px 14px")
                        property("margin", "4px 0")
                    }
                }) {
                    Span({ style { property("font-size", "22px") } }) { Text(Avatars.forPlayer(name)) }
                    Span({
                        classes("grow")
                        style {
                            property("font-size", "16px")
                            property("margin-left", "2px")
                        }
                    }) { Text(name) }
                    Button({
                        style {
                            property("flex", "none")
                            property("width", "28px")
                            property("height", "28px")
                            property("background", "var(--secondary)")
                            property("color", "var(--muted)")
                            property("border", "none")
                            property("border-radius", "50%")
                            property("font-size", "16px")
                            property("font-family", "inherit")
                            property("cursor", "pointer")
                            property("display", "flex")
                            property("align-items", "center")
                            property("justify-content", "center")
                        }
                        onClick { model.players.remove(name) }
                    }) { Text("×") }
                }
            }
            if (model.players.size < GameLogic.MIN_PLAYERS) {
                Div({
                    classes("muted")
                    style {
                        property("font-size", "12px")
                        property("margin-top", "8px")
                    }
                }) { Text(s.minPlayers(GameLogic.MIN_PLAYERS)) }
            }
        }

        PrimaryButton(s.continueNext, enabled = canNext) { model.screen = LocalScreen.THEME }
    }
}

// ---------------------------------------------------------------- THEME

@Composable
private fun LocalThemeScreen(model: LocalModel) {
    val s = I18n.strings
    Div({ classes("screen-body") }) {
        BackLink(s.backVoltar) { model.screen = LocalScreen.NAMES }
        StepIndicator(2)
        H2({ classes("title") }) { Text(s.chooseTheme) }
        Div({
            classes("muted")
            style { property("margin", "6px 0 12px") }
        }) { Text(s.themeWillBeDrawn) }

        Div({
            classes("grid2", "grow")
            style {
                property("align-content", "start")
                property("overflow-y", "auto")
                property("margin-bottom", "12px")
            }
        }) {
            Themes.localized(I18n.current).forEach { t ->
                val selected = t.id == model.themeId
                Button({
                    classes("card-select")
                    if (selected) classes("selected")
                    onClick { model.themeId = t.id }
                }) {
                    Div({ style { property("font-size", "30px") } }) { Text(t.emoji) }
                    Div({
                        style {
                            property("font-size", "14px")
                            property("font-weight", "500")
                            property("margin-top", "6px")
                        }
                    }) { Text(t.name) }
                }
            }
        }

        PrimaryButton(s.continueNext) { model.screen = LocalScreen.RULES }
    }
}

// ---------------------------------------------------------------- RULES

@Composable
private fun LocalRulesScreen(model: LocalModel) {
    val s = I18n.strings
    val maxInf = model.maxInfiltrators()
    Div({ classes("screen-body") }) {
        BackLink(s.backVoltar) { model.screen = LocalScreen.THEME }
        StepIndicator(3)

        H2({
            classes("title")
            style { property("font-size", "22px") }
        }) { Text(s.infiltratorsTitle) }
        Div({ style { property("height", "8px") } })
        LocalStepper(
            value = model.numInfiltrators.toString(),
            label = if (model.numInfiltrators == 1) s.infiltratorSingular else s.infiltratorPlural,
            onMinus = { model.numInfiltrators = (model.numInfiltrators - 1).coerceAtLeast(1) },
            onPlus = { model.numInfiltrators = (model.numInfiltrators + 1).coerceAtMost(maxInf) },
        )
        Div({
            classes("muted")
            style {
                property("font-size", "12px")
                property("margin-top", "6px")
            }
        }) { Text(s.needTwoCommons) }

        Div({ style { property("height", "20px") } })
        H2({
            classes("title")
            style { property("font-size", "22px") }
        }) { Text(s.discussionTimeTitle) }
        Div({ style { property("height", "8px") } })
        LocalStepper(
            value = formatTime(model.timerSeconds),
            onMinus = { model.timerSeconds = (model.timerSeconds - 30).coerceAtLeast(60) },
            onPlus = { model.timerSeconds = (model.timerSeconds + 30).coerceAtMost(600) },
        )

        Div({ classes("grow"); style { property("min-height", "28px") } })
        PrimaryButton(s.startMatch) {
            if (model.numInfiltrators > maxInf) model.numInfiltrators = maxInf
            model.startRound()
        }
    }
}

@Composable
private fun LocalStepper(
    value: String,
    label: String? = null,
    onMinus: () -> Unit,
    onPlus: () -> Unit,
) {
    Div({
        classes("row-between")
        style {
            property("background", "var(--card)")
            property("border-radius", "var(--radius-card)")
            property("padding", "12px")
        }
    }) {
        LocalRoundButton("−", onMinus)
        Div({ style { property("text-align", "center") } }) {
            Div({
                style {
                    property("font-size", "28px")
                    property("font-weight", "700")
                }
            }) { Text(value) }
            if (label != null) {
                Div({
                    classes("muted")
                    style {
                        property("font-size", "11px")
                        property("letter-spacing", "1px")
                    }
                }) { Text(label.uppercase()) }
            }
        }
        LocalRoundButton("+", onPlus)
    }
}

@Composable
private fun LocalRoundButton(symbol: String, onClick: () -> Unit) {
    Button({
        style {
            property("flex", "none")
            property("width", "48px")
            property("height", "48px")
            property("background", "var(--secondary)")
            property("color", "var(--on-secondary)")
            property("border", "none")
            property("border-radius", "50%")
            property("font-size", "22px")
            property("font-weight", "700")
            property("font-family", "inherit")
            property("cursor", "pointer")
        }
        onClick { onClick() }
    }) { Text(symbol) }
}

// ---------------------------------------------------------------- REVEAL

@Composable
private fun LocalRevealScreen(model: LocalModel, onExit: () -> Unit) {
    val s = I18n.strings
    val player = model.order[model.revealIndex]
    val isInfiltrator = player in model.infiltrators
    val accomplices = model.infiltrators.filter { it != player }
    val isLast = model.revealIndex + 1 >= model.order.size

    // SEGURAR pra espiar: pressionado mostra a carta real por baixo; soltar esconde.
    var holding by remember(model.revealIndex) { mutableStateOf(false) }
    var peeked by remember(model.revealIndex) { mutableStateOf(false) }

    Div({ classes("screen-body") }) {
        GameTopBar(s.revealPlayerXofN(model.revealIndex + 1, model.order.size)) { onExit() }

        Div({
            classes("grow")
            style {
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("padding", "8px 0")
            }
        }) {
            Div({
                style {
                    property("position", "relative")
                    property("width", "min(100%, 340px)")
                    property("aspect-ratio", "0.72")
                }
            }) {
                // Carta REAL (por baixo) — papel do jogador
                Div({
                    classes(if (isInfiltrator) "card-gradient-infiltrator" else "card-gradient-common")
                    style {
                        property("position", "absolute")
                        property("inset", "0")
                        property("border-radius", "var(--radius-card)")
                        property("padding", "24px")
                        property("display", "flex")
                        property("flex-direction", "column")
                        property("align-items", "center")
                        property("justify-content", "center")
                        property("text-align", "center")
                    }
                }) {
                    Div({ style { property("font-size", "44px") } }) { Text(Avatars.forPlayer(player)) }
                    Div({
                        style {
                            property("font-size", "14px")
                            property("color", "rgba(236, 237, 241, 0.85)")
                            property("margin", "8px 0")
                        }
                    }) { Text(player) }
                    if (isInfiltrator) {
                        Div({
                            style {
                                property("font-size", "11px")
                                property("letter-spacing", "3px")
                                property("color", "rgba(236, 237, 241, 0.8)")
                            }
                        }) { Text(s.youAre) }
                        Div({
                            style {
                                property("font-size", "34px")
                                property("font-weight", "900")
                            }
                        }) { Text(s.infiltrator) }
                        Div({ style { property("height", "12px") } })
                        Div({
                            style {
                                property("font-size", "13px")
                                property("color", "rgba(236, 237, 241, 0.8)")
                            }
                        }) { Text(s.hintLabel) }
                        Div({
                            style {
                                property("font-size", "22px")
                                property("font-weight", "700")
                            }
                        }) { Text(model.hint) }
                        if (accomplices.isNotEmpty()) {
                            Div({ style { property("height", "14px") } })
                            Div({
                                style {
                                    property("font-size", "12px")
                                    property("color", "rgba(236, 237, 241, 0.8)")
                                }
                            }) { Text(if (accomplices.size == 1) s.yourAccompliceSingular else s.yourAccomplicesPlural) }
                            Div({
                                style {
                                    property("font-size", "16px")
                                    property("font-weight", "700")
                                }
                            }) { Text(accomplices.joinToString(" · ")) }
                        }
                    } else {
                        Div({
                            style {
                                property("font-size", "11px")
                                property("letter-spacing", "3px")
                                property("color", "rgba(236, 237, 241, 0.8)")
                            }
                        }) { Text(s.yourWord) }
                        Div({
                            style {
                                property("font-size", "36px")
                                property("font-weight", "900")
                            }
                        }) { Text(model.word) }
                        Div({
                            style {
                                property("font-size", "12px")
                                property("color", "rgba(236, 237, 241, 0.8)")
                                property("margin-top", "12px")
                            }
                        }) { Text(s.themeWithEmoji(model.theme.emoji, model.theme.name)) }
                    }
                }

                // Carta CAPA (por cima) — segure pra espiar a carta real
                Div({
                    style {
                        property("position", "absolute")
                        property("inset", "0")
                        property("border-radius", "var(--radius-card)")
                        property("background", "var(--card-elevated)")
                        property("padding", "24px")
                        property("display", "flex")
                        property("flex-direction", "column")
                        property("align-items", "center")
                        property("justify-content", "center")
                        property("text-align", "center")
                        property("cursor", "pointer")
                        property("touch-action", "none")
                        property("user-select", "none")
                        property("-webkit-user-select", "none")
                        property("opacity", if (holding) "0" else "1")
                        property("transition", "opacity 0.15s ease")
                    }
                    // preventDefault no touchstart suprime os eventos de mouse sintéticos
                    // (evita disparo duplo) e o long-press/scroll do mobile.
                    onTouchStart { event ->
                        event.preventDefault()
                        holding = true
                        peeked = true
                    }
                    onTouchEnd { event ->
                        event.preventDefault()
                        holding = false
                    }
                    onTouchCancel { holding = false }
                    onMouseDown { event ->
                        event.preventDefault()
                        holding = true
                        peeked = true
                    }
                    onMouseUp { holding = false }
                    onMouseLeave { holding = false }
                    onContextMenu { event -> event.preventDefault() }
                }) {
                    Div({ style { property("font-size", "40px") } }) { Text(Avatars.forPlayer(player)) }
                    Div({
                        style {
                            property("font-size", "22px")
                            property("font-weight", "700")
                            property("margin-top", "10px")
                        }
                    }) { Text(player) }
                    Div({
                        classes("gold")
                        style {
                            property("font-size", "30px")
                            property("margin-top", "10px")
                        }
                    }) { Text("👆") }
                    Div({
                        classes("muted")
                        style { property("margin-top", "6px") }
                    }) { Text(s.revealHoldHint) }
                }
            }
        }

        Div({ style { property("height", "12px") } })
        PrimaryButton(
            if (isLast) s.startDiscussion else s.nextPlayer,
            enabled = peeked,
        ) {
            if (isLast) model.screen = LocalScreen.TIMER
            else model.revealIndex += 1
        }
    }
}

// ---------------------------------------------------------------- TIMER

@Composable
private fun LocalTimerScreen(model: LocalModel, onExit: () -> Unit) {
    val s = I18n.strings
    var remaining by remember { mutableStateOf(model.timerSeconds) }
    LaunchedEffect(Unit) {
        while (remaining > 0) {
            delay(1000)
            remaining -= 1
        }
        model.screen = LocalScreen.VOTE
    }
    val fraction = if (model.timerSeconds == 0) 0f else remaining.toFloat() / model.timerSeconds
    val degrees = (360f * fraction).toInt()

    Div({ classes("screen-body") }) {
        GameTopBar(s.discussionTimeTitle) { onExit() }
        Div({
            classes("grow")
            style {
                property("display", "flex")
                property("flex-direction", "column")
                property("align-items", "center")
                property("justify-content", "center")
            }
        }) {
            Div({
                style {
                    property("position", "relative")
                    property("width", "240px")
                    property("height", "240px")
                    property("display", "flex")
                    property("align-items", "center")
                    property("justify-content", "center")
                }
            }) {
                // Anel de progresso: conic-gradient mascarado pra virar um ring de 14px.
                Div({
                    style {
                        property("position", "absolute")
                        property("inset", "0")
                        property("border-radius", "50%")
                        property(
                            "background",
                            "conic-gradient(var(--primary) 0deg ${degrees}deg, var(--secondary) ${degrees}deg 360deg)",
                        )
                        property(
                            "-webkit-mask",
                            "radial-gradient(farthest-side, transparent calc(100% - 14px), #000 calc(100% - 13px))",
                        )
                        property(
                            "mask",
                            "radial-gradient(farthest-side, transparent calc(100% - 14px), #000 calc(100% - 13px))",
                        )
                    }
                })
                Span({
                    style {
                        property("position", "relative")
                        property("font-size", "52px")
                        property("font-weight", "900")
                    }
                }) { Text(formatTime(remaining)) }
            }
            Div({
                classes("muted", "text-center")
                style {
                    property("font-size", "14px")
                    property("margin-top", "28px")
                }
            }) { Text(s.discussionPrompt) }
            Div({ style { property("height", "24px") } })
            SecondaryButton(s.goToVote) { model.screen = LocalScreen.VOTE }
        }
    }
}

// ---------------------------------------------------------------- VOTE

@Composable
private fun LocalVoteScreen(model: LocalModel, onExit: () -> Unit) {
    val s = I18n.strings
    var voterIndex by remember { mutableStateOf(0) }
    val voter = model.order[voterIndex]
    val candidates = model.order.filter { it != voter }
    val selected = remember(voterIndex) { mutableStateListOf<String>() }
    val n = model.infiltrators.size
    val isLast = voterIndex + 1 >= model.order.size

    Div({ classes("screen-body") }) {
        GameTopBar(s.voteTitle) { onExit() }
        Div({
            classes("text-center")
            style {
                property("font-size", "26px")
                property("font-weight", "700")
                property("margin-top", "4px")
            }
        }) { Text(s.voteTurnOf(voter)) }
        Div({
            classes("muted", "text-center")
            style { property("margin", "6px 0 12px") }
        }) { Text(s.voteChooseSuspectsLocal(n)) }

        Div({
            classes("grid2", "grow")
            style {
                property("align-content", "start")
                property("overflow-y", "auto")
            }
        }) {
            candidates.forEach { cand ->
                val isSel = cand in selected
                Button({
                    classes("card-select")
                    if (isSel) classes("selected")
                    style {
                        property("display", "flex")
                        property("align-items", "center")
                        property("gap", "8px")
                        property("padding", "14px")
                        property("text-align", "left")
                    }
                    onClick {
                        if (isSel) selected.remove(cand)
                        else if (selected.size < n) selected.add(cand)
                    }
                }) {
                    Span({ style { property("font-size", "22px") } }) { Text(Avatars.forPlayer(cand)) }
                    Span({
                        style {
                            property("font-size", "15px")
                            property("font-weight", "700")
                            if (isSel) property("color", "var(--primary)")
                        }
                    }) { Text(cand) }
                }
            }
        }

        Div({
            classes("muted", "text-center")
            style { property("margin", "6px 0") }
        }) { Text(s.voteSelectedOfNSuffix(selected.size, n)) }
        PrimaryButton(
            if (isLast) s.voteConfirmAndResult else s.voteConfirmArrow,
            enabled = selected.size == n,
        ) {
            model.votes.add(Vote(voterId = voter, votedForIds = selected.toList()))
            if (isLast) model.finishVoting()
            else voterIndex += 1
        }
    }
}

// ---------------------------------------------------------------- RESULT

@Composable
private fun LocalResultScreen(model: LocalModel) {
    val s = I18n.strings
    val result = model.result ?: return
    val playersWon = result.playersWon

    Div({
        classes("screen-body")
        style { property("padding-top", "8px") }
    }) {
        Div({
            style {
                property("background", if (playersWon) "var(--gradient-win)" else "var(--gradient-lose)")
                property("border-radius", "var(--radius-card)")
                property("padding", "24px")
                property("text-align", "center")
            }
        }) {
            Div({ style { property("font-size", "44px") } }) {
                Text(if (playersWon) s.resultPlayersWonEmoji else s.resultInfiltratorWonEmoji)
            }
            Div({
                style {
                    property("font-size", "28px")
                    property("font-weight", "900")
                }
            }) { Text(if (playersWon) s.resultPlayersWon else s.resultInfiltratorWon) }
            Div({
                style {
                    property("font-size", "13px")
                    property("color", "rgba(236, 237, 241, 0.85)")
                    property("margin-top", "6px")
                }
            }) {
                Text(
                    if (playersWon) s.resultCaughtAll
                    else if (result.caughtAny) s.resultCaughtSome
                    else s.resultCaughtNone,
                )
            }
        }

        Div({ style { property("height", "16px") } })
        CardBox {
            Label(s.resultWordWas)
            Div({
                classes("text-center")
                style {
                    property("font-size", "26px")
                    property("font-weight", "700")
                    property("margin-top", "4px")
                }
            }) { Text("${model.theme.emoji} ${model.word}") }
        }

        Div({ style { property("height", "12px") } })
        CardBox {
            Label(if (model.infiltrators.size == 1) s.resultInfiltratorSingular else s.resultInfiltratorsPlural)
            Div({
                style {
                    property("display", "flex")
                    property("flex-wrap", "wrap")
                    property("gap", "8px")
                    property("justify-content", "center")
                    property("margin-top", "8px")
                }
            }) {
                model.infiltrators.forEach { name ->
                    Span({
                        classes("pill", "pill-danger")
                        style {
                            property("font-size", "14px")
                            property("padding", "6px 12px")
                        }
                    }) { Text(name) }
                }
            }
        }

        Div({ style { property("height", "12px") } })
        CardBox {
            Label(s.resultVotesLabel)
            Div({ style { property("margin-top", "8px") } }) {
                result.tally.entries.sortedByDescending { it.value }.forEach { (name, count) ->
                    val isInf = name in model.infiltrators
                    Div({
                        classes("row-between")
                        style { property("padding", "3px 0") }
                    }) {
                        Span({
                            style {
                                property("font-size", "15px")
                                if (isInf) {
                                    property("color", "var(--danger)")
                                    property("font-weight", "700")
                                }
                            }
                        }) { Text(name) }
                        Span({
                            classes("muted")
                            style { property("font-size", "14px") }
                        }) { Text(s.votesCountLocal(count)) }
                    }
                }
            }
        }

        Div({ style { property("height", "20px") } })
        PrimaryButton(s.playAgain) { model.startRound() }
        Div({ style { property("height", "10px") } })
        SecondaryButton(s.configureNewGame) { model.screen = LocalScreen.NAMES }
    }
}
